import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  getEmergencyContacts,
  saveEmergencyContact,
  deleteEmergencyContact,
} from 'portal-modules/contacts/api/api'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { addError } from 'portal-modules/common/redux/modules/errors'
import ERROR_MSGS from 'portal-modules/auth/constants/error-message'

// action types
export const ADD_CONTACT = 'rp/emergencyContacts/ADD_CONTACT'
export const CONTACT_ADDED = 'rp/emergencyContacts/CONTACT_ADDED'
export const DELETE_CONTACT = 'rp/emergencyContacts/DELETE_CONTACT'
export const CONTACT_DELETED = 'rp/emergencyContacts/CONTACT_DELETED'
export const REQUEST_CONTACTS = 'rp/emergencyContacts/REQUEST_CONTACTS'
export const RECEIVE_CONTACTS = 'rp/emergencyContacts/RECEIVE_CONTACTS'
export const UPDATE_CONTACT = 'rp/emergencyContacts/UPDATE_CONTACT'
export const CONTACT_UPDATED = 'rp/emergencyContacts/CONTACT_UPDATED'

// reducer
export default (state = {}, { type, payload }) => {
  switch (type) {
    case CONTACT_ADDED:
      return {
        ...state,
        [payload.id]: { ...payload },
      }
    case CONTACT_DELETED:
      return Object.keys(state).reduce((obj, key) => {
        if (key == payload) return obj
        return { ...obj, [key]: state[key] }
      }, {})
    case RECEIVE_CONTACTS:
      return {
        ...state,
        ...payload,
      }
    case CONTACT_UPDATED:
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          ...payload,
        },
      }
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const requestContacts = () => ({ type: REQUEST_CONTACTS })

// payload = contact w/o id
export const addContact = createFormAction(ADD_CONTACT)

// payload = string|number (id)
export const deleteContact = payload => ({
  type: DELETE_CONTACT,
  payload,
})

// payload = { id: string|number, updates: object }
export const updateContact = createFormAction(UPDATE_CONTACT)

// sagas
export function* fetchContacts() {
  const contacts = yield call(getEmergencyContacts)

  if (contacts.length) {
    yield put({
      type: RECEIVE_CONTACTS,
      payload: contacts.reduce(
        (obj, contact) => ({
          ...obj,
          [contact.id]: contact,
        }),
        {},
      ),
    })
  }
}

export function* postAddOrUpdateContact({ payload, type }) {
  const { emergency_contact } = yield call(saveEmergencyContact, payload)

  if (emergency_contact) {
    yield type === ADD_CONTACT
      ? put({ type: CONTACT_ADDED, payload: emergency_contact })
      : put({ type: CONTACT_UPDATED, payload })
  }
}

export function* postDeleteContact({ payload }) {
  yield call(deleteEmergencyContact, { id: payload })
  yield put({ type: CONTACT_DELETED, payload })
}

export function* watchRequestContacts() {
  yield takeLatest(
    REQUEST_CONTACTS,
    createRequestSaga(fetchContacts, {
      key: 'emergencyContacts',
      message: __('Looking for your contacts'),
    }),
  )
}

export function* watchAddOrUpdateContact() {
  yield takeEvery(
    [ADD_CONTACT, UPDATE_CONTACT],
    createRequestSaga(postAddOrUpdateContact, {
      key: 'emergencyContacts',
      message: __('Saving contact'),
      successMessage: __('Contact saved successfully'),
      onError: function* handleAddOrUpdateContactError({ code, fields, message }, { key }) {
        switch (code) {
          case 1002:
            yield put(addError({ key, message: fields?.length && `${message} ${fields?.[0]?.message}` || message }))
            break
          default:
            yield put(
              addError({
                key,
                message: ERROR_MSGS[code] || __('Something went wrong.'),
              }),
            )
            break
        }
      },
      write: true,
    }),
  )
}

export function* watchDeleteContact() {
  yield takeEvery(
    DELETE_CONTACT,
    createRequestSaga(postDeleteContact, {
      key: 'emergencyContacts',
      message: __('Deleting contact'),
      successMessage: __('Contact deleted successfully'),
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestContacts,
  watchAddOrUpdateContact,
  watchDeleteContact,
]
